/* eslint no-console: 0 */
import compareAsc from 'date-fns/compareAsc';
import fromUnixTime from 'date-fns/fromUnixTime';
import * as types from '../mutation-types';
import Report from 'dashboard/api/reports';
import { downloadCsv } from 'dashboard/helper/commons';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  messageData: [],
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  accountCircle: {},
  userReport: {
    isFetching: false,
    data: [],
  },
  userSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    outgoing_messages_count: 0,
    resolutions_count: 0,
  },
  channelSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    resolutions_count: 0,
  },
  channelReport: {
    isFetching: false,
    data: [],
  },
  labelSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    resolutions_count: 0,
  },
  labelReport: {
    isFetching: false,
    data: [],
  },
  userTable: [],
  teamSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    resolutions_count: 0,
  },
  teamReport: {
    isFetching: false,
    data: [],
  },
  periodSummary: {
    conversationsCount: 0,
    agentDialogueCount: 0,
    botDialogueCount: 0,
    minuteCount: 0,
    newQueueCount: 0,
    waitQueueCount: 0,
  },
  periodReport: [],
  workhoursSummary: {
    clocked_count: 0,
    offline_time: 0,
    online_time: 0,
    leave_time: 0,
    conversation_count: 0,
    dialogue_time: 0,
    total_appraise: 0,
  },
  workhoursReport: [],
  dialogSummary: {},
  dialogReport: {
    isFetching: false,
    data: [],
  },
  serviceCommentSummary: {},
  serviceCommentReport: [],
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
  },
};

const getters = {
  messageData(_state) {
    return _state.messageData;
  },
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getAccountCircle(_state) {
    return _state.accountCircle;
  },
  getUserTable(_state) {
    return _state.userTable;
  },
  getUserReports(_state) {
    return _state.userReport;
  },
  getUserSummary(_state) {
    return _state.userSummary;
  },
  getChannelSummary(_state) {
    return _state.channelSummary;
  },
  getChannelReports(_state) {
    return _state.channelReport;
  },
  getLabelSummary(_state) {
    return _state.labelSummary;
  },
  getLabelReports(_state) {
    return _state.labelReport;
  },
  getTeamSummary(_state) {
    return _state.teamSummary;
  },
  getTeamReports(_state) {
    return _state.teamReport;
  },
  getPeriodSummary(_state) {
    return _state.periodSummary;
  },
  getPeriodReports(_state) {
    return _state.periodReport;
  },
  getWorkhoursSummary(_state) {
    return _state.workhoursSummary;
  },
  getWorkhoursReports(_state) {
    return _state.workhoursReport;
  },
  getDialogSummary(_state) {
    return _state.dialogSummary;
  },
  getDialogReports(_state) {
    return _state.dialogReport;
  },
  getServiceCommentSummary(_state) {
    return _state.serviceCommentSummary;
  },
  getServiceCommentReports(_state) {
    return _state.serviceCommentReport;
  },

  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
};

export const actions = {
  async getMessageData({ commit }, { id } = {}) {
    try {
      const response = await Report.getMessageData({ id });
      commit(types.default.CONVERSATION_MESSAGE_DATA, response.data);
    } catch (error) {
      console.error(error);
    }
  },
  fetchAccountReport({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric: reportObj.metric,
      value: true,
    });
    Report.getAccountReports(
      reportObj.metric,
      reportObj.from,
      reportObj.to,
      reportObj.agents,
      reportObj.channels
    ).then(accountReport => {
      let { data } = accountReport;
      data = data.filter(
        el => compareAsc(new Date(), fromUnixTime(el.timestamp)) > -1
      );
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric: reportObj.metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric: reportObj.metric,
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getAccountSummary(
      reportObj.from,
      reportObj.to,
      reportObj.agents,
      reportObj.channels
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountCircle({ state: $state }, reportObj) {
    Report.getAccountCircle(
      reportObj.from,
      reportObj.to,
      reportObj.agents,
      reportObj.channels
    )
      .then(accountCircle => {
        $state.accountCircle = accountCircle.data;
      })
      .catch(err => {
        console.log(err);
      });
  },
  fetchUserTable({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, true);
    Report.getUserTable(
      reportObj.from,
      reportObj.to,
      reportObj.agents,
      reportObj.channels
    )
      .then(UserTable => {
        commit(types.default.SET_USER_TABLE, UserTable.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchChannelSummary({ commit }, reportObj) {
    Report.getChannelSummary(
      reportObj.from,
      reportObj.to,
      reportObj.channels,
      reportObj.teams
    )
      .then(channelSummary => {
        commit(types.default.SET_CHANNEL_SUMMARY, channelSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchChannelReport({ commit }, reportObj) {
    commit(types.default.TOGGLE_CHANNEL_REPORT_LOADING, true);
    Report.getChannelReports(
      reportObj.metric,
      reportObj.from,
      reportObj.to,
      reportObj.channels,
      reportObj.teams
    ).then(channelReport => {
      let { data } = channelReport;
      commit(types.default.SET_CHANNEL_REPORTS, data);
      commit(types.default.TOGGLE_CHANNEL_REPORT_LOADING, false);
    });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type, reportObj.page)
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchLabelSummary({ commit }, { from, to }) {
    Report.getLabelSummary(from, to)
      .then(labelSummary => {
        commit(types.default.SET_LABEL_SUMMARY, labelSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_LABEL_REPORT_LOADING, false);
      });
  },
  fetchLabelReport({ commit }, { metric, from, to, searchkey, channels }) {
    commit(types.default.TOGGLE_LABEL_REPORT_LOADING, true);
    Report.getLabelReports(metric, from, to, searchkey, channels).then(
      labelReport => {
        let { data } = labelReport;
        commit(types.default.SET_LABEL_REPORTS, data);
        commit(types.default.TOGGLE_LABEL_REPORT_LOADING, false);
      }
    );
  },
  fetchTeamSummary({ commit }, reportObj) {
    Report.getTeamSummary(
      reportObj.from,
      reportObj.to,
      reportObj.teams,
      reportObj.channels
    )
      .then(teamSummary => {
        commit(types.default.SET_TEAM_SUMMARY, teamSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_TEAM_REPORT_LOADING, false);
      });
  },
  fetchTeamReport({ commit }, reportObj) {
    commit(types.default.TOGGLE_TEAM_REPORT_LOADING, true);
    Report.getTeamReports(
      reportObj.metric,
      reportObj.from,
      reportObj.to,
      reportObj.teams,
      reportObj.channels
    ).then(teamReport => {
      let { data } = teamReport;
      commit(types.default.TOGGLE_TEAM_REPORT_LOADING, false);
      commit(types.default.SET_TEAM_REPORTS, data);
    });
  },
  fetchPeriodSummary: async ({ commit }, { from, to, agents, channels }) => {
    try {
      const response = await Report.getPeriodSummary(
        from,
        to,
        agents,
        channels
      );
      commit(types.default.SET_PERIOD_SUMMARY, response.data);
    } catch (error) {
      // Ignore error
    }
  },
  fetchPeriodReports: async ({ commit }, { from, to, agents, channels }) => {
    try {
      const response = await Report.getPeriodReports(
        from,
        to,
        agents,
        channels
      );
      commit(types.default.SET_PERIOD_REPORTS, response.data);
    } catch (error) {
      // to do something
    }
  },
  fetchWorkhoursSummary: async ({ commit }, { from, to, agents }) => {
    try {
      const response = await Report.getWorkhoursSummary(from, to, agents);
      commit(types.default.WORK_HOURS_SUMMARY, response.data);
    } catch (error) {
      // to do something
    }
  },
  fetchWorkhoursReports: async ({ commit }, { from, to, agents }) => {
    try {
      const response = await Report.getWorkhoursReports(from, to, agents);
      commit(types.default.WORK_HOURS_REPORTS, response.data);
    } catch (error) {
      // to do something
    }
  },
  fetchDialogSummary({ state: $state }, { from, to, agents, channels }) {
    Report.getDialogSummary(from, to, agents, channels)
      .then(data => {
        $state.dialogSummary = data.data;
      })
      .catch(error => {
        console.error(error);
      });
  },
  fetchDialogReports({ state: $state }, { from, to, agents, channels }) {
    Report.getDialogReports(from, to, agents, channels)
      .then(data => {
        $state.dialogReport.data = data.data;
      })
      .catch(error => {
        console.error(error);
      });
  },
  fetchServiceCommentSummary: async (
    { commit },
    { from, to, agents, channels, teams, csat }
  ) => {
    try {
      const response = await Report.getServiceCommentSummary(
        from,
        to,
        agents,
        channels,
        teams,
        csat
      );
      commit(types.default.SERVICE_COMMENT_SUMMARY, response.data);
    } catch (error) {
      // to do something
    }
  },
  fetchServiceCommentReports: async (
    { commit },
    { from, to, agents, channels, teams, csat }
  ) => {
    try {
      const response = await Report.getServiceCommentReports(
        from,
        to,
        agents,
        channels,
        teams,
        csat
      );
      commit(types.default.SERVICE_COMMENT_REPORTS, response.data);
    } catch (error) {
      // to do something
    }
  },
  downloadDialogReports(_, reportObj) {
    return Report.downloadDialogReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.downloadLabelReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.downloadTeamReports(reportObj.from, reportObj.to)
      .then(response => {
        let csvContent = 'data:text/csv;charset=utf-8,' + response.data;
        var encodedUri = encodeURI(csvContent);
        window.open(encodedUri);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamreports(_, reportObj) {
    return Report.downloadTeamreports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadChannelReports(_, reportObj) {
    return Report.downloadChannelReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadServiceCommentReports(_, reportObj) {
    return Report.downloadServiceCommentReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadWorkhoursReports(_, reportObj) {
    return Report.downloadWorkhoursReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadPeriodReports(_, reportObj) {
    return Report.downloadPeriodReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },

  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        downloadCsv(response);
      })
      .catch(error => {
        console.error(error);
      });
  },
};

const mutations = {
  [types.default.CONVERSATION_MESSAGE_DATA](_state, messageData) {
    _state.messageData = messageData;
  },
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },

  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_USER_TABLE](_state, data) {
    _state.userTable = data;
  },
  [types.default.SET_USER_REPORTS](_state, userData) {
    _state.userReport.data = userData;
  },
  [types.default.SET_USER_SUMMARY](_state, summaryData) {
    _state.userSummary = summaryData;
  },
  [types.default.TOGGLE_USER_REPORT_LOADING](_state, flag) {
    _state.userReport.isFetching = flag;
  },
  [types.default.SET_CHANNEL_SUMMARY](_state, summaryData) {
    _state.channelSummary = summaryData;
  },
  [types.default.SET_CHANNEL_REPORTS](_state, userData) {
    _state.channelReport.data = userData;
  },
  [types.default.TOGGLE_CHANNEL_REPORT_LOADING](_state, flag) {
    _state.channelReport.isFetching = flag;
  },
  [types.default.SET_LABEL_SUMMARY](_state, summaryData) {
    _state.labelSummary = summaryData;
  },
  [types.default.SET_LABEL_REPORTS](_state, userData) {
    _state.labelReport.data = userData;
  },
  [types.default.TOGGLE_LABEL_REPORT_LOADING](_state, flag) {
    _state.labelReport.isFetching = flag;
  },
  [types.default.SET_USER_TABLE](_state, data) {
    _state.userTable = data;
  },
  [types.default.SET_TEAM_SUMMARY](_state, summaryData) {
    _state.teamSummary = summaryData;
  },
  [types.default.SET_TEAM_REPORTS](_state, userData) {
    _state.teamReport.data = userData;
  },
  [types.default.TOGGLE_TEAM_REPORT_LOADING](_state, flag) {
    _state.teamReport.isFetching = flag;
  },
  [types.default.SET_PERIOD_SUMMARY](
    _state,
    {
      conversations_count: conversationsCount,
      agent_dialogue_count: agentDialogueCount,
      bot_dialogue_count: botDialogueCount,
      minute_count: minuteCount,
      new_queue_count: newQueueCount,
      wait_queue_count: waitQueueCount,
    }
  ) {
    _state.periodSummary.conversationsCount = conversationsCount || 0;
    _state.periodSummary.agentDialogueCount = agentDialogueCount || 0;
    _state.periodSummary.botDialogueCount = botDialogueCount || 0;
    _state.periodSummary.minuteCount = minuteCount || 0;
    _state.periodSummary.newQueueCount = newQueueCount || 0;
    _state.periodSummary.waitQueueCount = waitQueueCount || 0;
  },
  [types.default.SET_PERIOD_REPORTS](_state, { period }) {
    _state.periodReport = period;
  },
  [types.default.WORK_HOURS_REPORTS](_state, data) {
    _state.workhoursReport = data;
  },
  [types.default.WORK_HOURS_SUMMARY](_state, data) {
    _state.workhoursSummary = data;
  },
  [types.default.SERVICE_COMMENT_SUMMARY](_state, data) {
    _state.serviceCommentSummary = data;
  },
  [types.default.SERVICE_COMMENT_REPORTS](_state, data) {
    _state.serviceCommentReport = data;
  },
  [types.default.SET_ENTERPRISE_SUMMARY](_state, data) {
    _state.enterpriseSummary = data;
  },
  [types.default.SET_ENTERPRISE_REPORTS](_state, data) {
    _state.enterpriseReport = data;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
